// core/data/council.ts — Static Council chrome (dashboard display data).
//
// Faithfully ported from legacy src/scripts/data/council.js.
// This module is purely static display data for the Council of Moderators UI.
// It does NOT contain the state machine — that lives in core/services/casesService.ts.
// The live cases array lives in core/stores/caseStore.ts (hydrated from core/data/cases.ts).

import type { CouncilTeam } from '@/core/models/case';

// Tone type for stat card left-border accent.
export type StatTone = 'blue' | 'green' | 'violet' | 'cyan' | 'amber' | 'pink';

export interface CouncilStat {
  label: string;
  value: string;
  sub: string;
  tone: StatTone;
}

export interface PipelineStage {
  id: string;
  name: string;
  count: number;
  blurb: string;
}

export interface DailyReport {
  label: string;
  value: string;
}

export interface RecentDecision {
  caseId: string;
  summary: string;
  outcome: 'removed' | 'kept';
}

export interface AppealRecord {
  caseId: string;
  summary: string;
  status: string;
}

// Top stat cards.
export const councilStats: CouncilStat[] = [
  { label: 'Pending Cases',    value: '1,247',  sub: '+8% today',       tone: 'blue' },
  { label: 'Resolved Today',   value: '389',    sub: '94% consensus',   tone: 'green' },
  { label: 'Active Teams',     value: '12',     sub: 'rotating',        tone: 'violet' },
  { label: 'Avg Resolve Time', value: '2h 14m', sub: '-12m vs avg',     tone: 'cyan' },
  { label: 'Open Appeals',     value: '27',     sub: 'bias review',     tone: 'amber' },
  { label: 'Consistency',      value: '96%',    sub: 'team agreement',  tone: 'pink' },
];

// Consistency Check → AI Review → Human Review.
export const pipelineStages: PipelineStage[] = [
  { id: 'consistency',  name: 'Consistency Check', count: 312, blurb: 'Auto-screens reports against the five categories' },
  { id: 'ai-review',    name: 'AI Review',         count: 184, blurb: 'AI triage flags likely violations for humans' },
  { id: 'human-review', name: 'Human Review',      count: 91,  blurb: 'A rotating team votes on the outcome' },
];

// Rotating randomized teams. av keys reuse the existing .av-grad palette (av-1..av-5, av-grad).
export const teams: CouncilTeam[] = [
  {
    id: 'alpha',
    name: 'Team Alpha',
    status: 'On rotation',
    caseload: 34,
    members: [
      { name: 'WayneTech',    av: 'av-grad' },
      { name: 'OpenMind',     av: 'av-3' },
      { name: 'LibertyMind',  av: 'av-2' },
      { name: 'TruthWarrior', av: 'av-4' },
    ],
  },
  {
    id: 'bravo',
    name: 'Team Bravo',
    status: 'On rotation',
    caseload: 28,
    members: [
      { name: 'FreedomSeeker',  av: 'av-1' },
      { name: 'OpenDialogue',   av: 'av-5' },
      { name: 'FreeThinker',    av: 'av-4' },
    ],
  },
  {
    id: 'charlie',
    name: 'Team Charlie',
    status: 'Standby',
    caseload: 19,
    members: [
      { name: 'TechVisionary', av: 'av-4' },
      { name: 'SeenAndUnseen', av: 'av-2' },
      { name: 'LibertyVoice',  av: 'av-5' },
    ],
  },
];

// The `dailyReports` seed that used to live here is gone. It supplied the Council
// sidebar with "Reports filed 1,842 / AI-cleared 1,231 / Escalated to humans 611"
// on every Council screen, next to an Overview panel reading the real counts from
// the API — so the two disagreed permanently, and one of them named a system that
// does not exist. Overview builds its rows from useCouncilOverview() instead.
// The DailyReport interface above is still the row shape it uses.

// Decorative only — these caseId strings are illustrative and NOT tied to the live store.
export const recentDecisions: RecentDecision[] = [
  { caseId: 'case-005', summary: 'Terrorism glorification',  outcome: 'removed' },
  { caseId: 'case-001', summary: 'Crypto giveaway scam',     outcome: 'removed' },
  { caseId: 'case-006', summary: 'Scam report: legitimate', outcome: 'kept' },
  { caseId: 'case-009', summary: 'Coordinated abuse report', outcome: 'kept' },
];

export const appealRecords: AppealRecord[] = [
  { caseId: 'case-006', summary: 'Scam ruling appealed',  status: 'Under bias review' },
  { caseId: 'case-009', summary: 'Abuse ruling appealed', status: 'Upheld' },
];
